/**
 * Permission Calculator - Advanced Permission Logic
 * 
 * Handles complex permission calculations and validations
 */

import { 
  MODULES, 
  ACTIONS, 
  PERMISSION_PROFILES,
  calculateEffectivePermissions,
  hasPermission,
  hasModuleAccess,
  canAccessRoute,
  checkCompanyLimits
} from './permissions';

/**
 * Permission Calculator Class
 * Handles all permission-related calculations for a user
 */
export class PermissionCalculator {
  constructor(userPermissions, companyLimits = null) {
    this.userPermissions = userPermissions;
    this.companyLimits = companyLimits;
    this.effectivePermissions = this.calculateEffective();
  }

  /**
   * Calculate effective permissions from profile and custom permissions
   */
  calculateEffective() {
    if (!this.userPermissions) return [];

    const { profileId, customPermissions = [] } = this.userPermissions;
    const profile = profileId ? PERMISSION_PROFILES[profileId.toUpperCase().replace('-', '_')] : null;
    
    let effective = calculateEffectivePermissions(profile, customPermissions);
    
    // Apply company limits if they exist
    if (this.companyLimits) {
      effective = this.applyCompanyLimits(effective);
    }
    
    return effective;
  }

  /**
   * Apply company limits to permissions
   */
  applyCompanyLimits(permissions) {
    if (!this.companyLimits || !Array.isArray(this.companyLimits)) {
      return permissions;
    }

    return permissions.map(permission => {
      const limit = this.companyLimits.find(limit => limit.module === permission.module);
      if (!limit) {
        // Module not allowed by company
        return null;
      }

      // Filter actions to only those allowed by company
      const allowedActions = permission.actions.filter(action => 
        limit.actions.includes(action)
      );

      return {
        module: permission.module,
        actions: allowedActions
      };
    }).filter(Boolean); // Remove null entries
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(module, action) {
    return hasPermission(this.effectivePermissions, module, action);
  }

  /**
   * Check if user has module access
   */
  hasModuleAccess(module) {
    return hasModuleAccess(this.effectivePermissions, module);
  }

  /**
   * Check if user can access route
   */
  canAccessRoute(route) {
    return canAccessRoute(this.effectivePermissions, route);
  }

  /**
   * Get all accessible modules
   */
  getAccessibleModules() {
    return this.effectivePermissions
      .filter(perm => perm.actions.length > 0)
      .map(perm => perm.module);
  }

  /**
   * Get actions for specific module
   */
  getModuleActions(module) {
    const modulePermission = this.effectivePermissions.find(
      perm => perm.module === module
    );
    return modulePermission ? modulePermission.actions : [];
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissionChecks) {
    return permissionChecks.some(({ module, action }) => 
      this.hasPermission(module, action)
    );
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissionChecks) {
    return permissionChecks.every(({ module, action }) => 
      this.hasPermission(module, action)
    );
  }

  /**
   * Check document type access based on permissions
   */
  canAccessDocumentType(documentType) {
    switch (documentType) {
      case 'albaran':
        return this.hasModuleAccess(MODULES.ALBARANES);
      case 'parte_trabajo':
        return this.hasModuleAccess(MODULES.PARTES_TRABAJO);
      default:
        return false;
    }
  }

  /**
   * Check if user can create specific document type
   */
  canCreateDocumentType(documentType) {
    switch (documentType) {
      case 'albaran':
        return this.hasPermission(MODULES.ALBARANES, ACTIONS.CREATE);
      case 'parte_trabajo':
        return this.hasPermission(MODULES.PARTES_TRABAJO, ACTIONS.CREATE);
      default:
        return false;
    }
  }

  /**
   * Get allowed document types for user
   */
  getAllowedDocumentTypes() {
    const types = [];
    
    if (this.hasModuleAccess(MODULES.ALBARANES)) {
      types.push('albaran');
    }
    
    if (this.hasModuleAccess(MODULES.PARTES_TRABAJO)) {
      types.push('parte_trabajo');
    }
    
    // PRL está disponible para todos los usuarios que tengan acceso a documentos
    if (this.hasModuleAccess(MODULES.DOCUMENTOS) || this.hasModuleAccess(MODULES.PRL) || this.isSuperAdmin) {
      types.push('prl');
    }
    
    return types;
  }

  /**
   * Check if user should see navigation item
   */
  shouldShowNavItem(navItem) {
    const navPermissions = {
      'upload': [
        { module: MODULES.ALBARANES, action: ACTIONS.CREATE },
        { module: MODULES.PARTES_TRABAJO, action: ACTIONS.CREATE }
      ],
      'docs': [
        { module: MODULES.DOCUMENTOS, action: ACTIONS.READ }
      ],
      'solicitudes': [
        { module: MODULES.SOLICITUDES, action: ACTIONS.READ }
      ],
      'albaranes': [
        { module: MODULES.ALBARANES, action: ACTIONS.READ }
      ],
      'admin': [
        { module: MODULES.USUARIOS, action: ACTIONS.READ },
        { module: MODULES.CONFIGURACION, action: ACTIONS.READ }
      ],
      'super-admin': [
        { module: MODULES.SUPER_ADMIN, action: ACTIONS.MANAGE }
      ]
    };

    const requiredPermissions = navPermissions[navItem];
    if (!requiredPermissions) return true;

    return this.hasAnyPermission(requiredPermissions);
  }

  /**
   * Get permission summary for debugging
   */
  getPermissionSummary() {
    return {
      profileId: this.userPermissions?.profileId,
      customPermissions: this.userPermissions?.customPermissions || [],
      effectivePermissions: this.effectivePermissions,
      accessibleModules: this.getAccessibleModules(),
      allowedDocumentTypes: this.getAllowedDocumentTypes(),
      companyLimitsApplied: !!this.companyLimits
    };
  }

  /**
   * Validate if permissions are within company limits
   */
  validateAgainstCompanyLimits() {
    if (!this.companyLimits) {
      return { isValid: true, violations: [] };
    }

    return checkCompanyLimits(this.effectivePermissions, this.companyLimits);
  }
}

/**
 * Factory function to create permission calculator
 */
export function createPermissionCalculator(userPermissions, companyLimits = null) {
  return new PermissionCalculator(userPermissions, companyLimits);
}

/**
 * Helper function to get default permissions for role
 */
export function getDefaultPermissionsForRole(role) {
  switch (role) {
    case 'super_admin':
      return {
        profileId: 'admin-completo',
        customPermissions: [
          {
            module: MODULES.SUPER_ADMIN,
            actions: [ACTIONS.MANAGE]
          },
          {
            module: MODULES.EMPRESAS,
            actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE]
          }
        ]
      };
    
    case 'company_admin':
      return {
        profileId: 'admin-completo',
        customPermissions: []
      };
    
    case 'company_user':
      return {
        profileId: 'supervisor-partes',
        customPermissions: [
          {
            module: MODULES.ALBARANES,
            actions: [ACTIONS.READ, ACTIONS.CREATE]
          },
          {
            module: MODULES.DOCUMENTOS,
            actions: [ACTIONS.READ]
          }
        ]
      };
    
    default:
      return {
        profileId: 'supervisor-partes',
        customPermissions: [
          {
            module: MODULES.DOCUMENTOS,
            actions: [ACTIONS.READ]
          }
        ]
      };
  }
}

/**
 * Helper to merge multiple permission sets
 */
export function mergePermissions(...permissionSets) {
  const merged = {};
  
  permissionSets.forEach(permissions => {
    if (!permissions || !Array.isArray(permissions)) return;
    
    permissions.forEach(permission => {
      if (!merged[permission.module]) {
        merged[permission.module] = new Set();
      }
      
      permission.actions.forEach(action => {
        merged[permission.module].add(action);
      });
    });
  });
  
  return Object.entries(merged).map(([module, actions]) => ({
    module,
    actions: Array.from(actions)
  }));
}